import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedNumericOperators,
  advancedOperators,
  runPickerOperators,
  advancedEnumOperators,
  advancedDatePickerOperator
} from '../../Compounds/UpstackDataGrid/helpers';
import { chipStyles, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { Adjustment } from '../../../store/Adjustment';
import { RunStatus } from '../../../store/Run';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers';
import { DrawerState } from '../../../store/Drawer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { Text } from '../../Atoms/Text';
import { handleAssetClick } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

const adjustmentCurrencyColumn = (
  field: keyof Pick<Adjustment, 'grossComm' | 'salesComm' | 'netBilled'>,
  headerName: string
): GridCustomColDef => ({
  type: 'singleSelect',
  valueOptions: [],
  getOptionLabel: (value) => String(value),
  getOptionValue: (value) => value,
  field,
  headerName,
  headerAlign: 'right',
  align: 'right',
  sortable: true,
  filterable: true,
  minWidth: 100,
  flex: 0.5,
  description: 'Drag to move column',
  filterOperators: advancedNumericOperators(),
  valueFormatter: (value) => value,
  renderCell: (params: GridCellParams<Adjustment>) => {
    if (params.value == null) return <span className="text-gray-600">—</span>;
    return (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    );
  }
});

export const DefaultAdjustmentGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={
          {
            id: v4(),
            data: params.row,
            ...getMetaData({
              name: ComponentName.ADJUSTMENT,
              id: String(params.row.commissionAdjustmentId)
            })
          } as DrawerState
        }
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true,
    disableExport: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'runDate',
    headerName: 'Run',
    sortable: true,
    filterable: true,
    minWidth: 80,
    flex: 0.5,
    description: 'Drag to move column',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: RunStatus ? Object.values(RunStatus) : [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'runStatus',
    headerName: 'Run Status',
    align: 'center',
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 0.7,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators(),
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    }
  },
  {
    field: 'assetNumber',
    headerName: 'Asset',
    sortable: true,
    filterable: true,
    width: 200,
    filterOperators: advancedOperators(),
    renderCell: (params: GridCellParams<Adjustment>) => {
      const assetId = params.row.assetUpstackId;
      if (!assetId) return '-';
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(assetId, ComponentName.ASSET)}>
          <Text
            size="sm14"
            color="indigo">
            {params?.row.assetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => `Adj - ${String(value)}`,
    getOptionValue: (value) => value,
    field: 'commissionAdjustmentId',
    headerName: 'Adjustment Number',
    sortable: true,
    filterable: true,
    minWidth: 140,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'rpmSupplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    minWidth: 180,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'agencyName',
    headerName: 'Agency',
    sortable: true,
    filterable: true,
    minWidth: 180,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'adjustmentType',
    headerName: 'Adjustment Type',
    sortable: true,
    filterable: true,
    minWidth: 120,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  adjustmentCurrencyColumn('grossComm', 'Gross Comm'),
  adjustmentCurrencyColumn('salesComm', 'Sales Comm'),
  adjustmentCurrencyColumn('netBilled', 'Net Billed'),
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'adjustmentNote',
    headerName: 'Adjustment Note',
    sortable: true,
    filterable: true,
    minWidth: 220,
    flex: 1.5,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'note',
    headerName: 'Tag Note',
    sortable: true,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    valueFormatter: (value: string | null, row: Adjustment) => {
      if (!row.adjustmentTagId) return '-';
      return value;
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'taggedBy.name',
    headerName: 'Tagged By',
    sortable: true,
    filterable: true,
    minWidth: 250,
    flex: 1.5,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    valueGetter: (value: string | null, row: Adjustment) => {
      if (!row.adjustmentTagId) return '-';
      const taggedBy = row.taggedBy;
      if (!taggedBy) return '';
      return `${taggedBy.firstName} ${taggedBy.lastName}`.trim();
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'taggedAt',
    headerName: 'Tagged At',
    sortable: true,
    filterable: true,
    minWidth: 120,
    flex: 0.7,
    description: 'Drag to move column',
    valueFormatter: (value: string | null, row: Adjustment) => {
      if (!row.adjustmentTagId) return '-';
      return getFormattedDate(value);
    },
    filterOperators: advancedDatePickerOperator()
  }
];
